/**
 * Chat — Phase-1 conversational pivot.
 *
 * Wraps the existing deterministic KP engine inside a live chat surface
 * with per-minute wallet billing. The component runs in two phases:
 *
 *   PHASE 'intake'  — birth profile + horary number + first question
 *   PHASE 'chat'    — live timer, ₹2.50/6s tick, messages, recharge sheet
 *
 * Billing is server-arbitrated (POST /chat/tick); the client only owns
 * the 6-second heartbeat and the display timer.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, readErr } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from '@/lib/toast';
import { fetchRegion, formatPrice } from '@/lib/region';
import { useRecharge } from '@/components/RechargeProvider';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ProfilePicker from '@/components/ProfilePicker';
import { Loader2, Sparkles, Clock, Wallet, Send, Star } from 'lucide-react';
import RectifyBirthTimeModal from '@/components/RectifyBirthTimeModal';

const TICK_INTERVAL_MS = 6000;
const PRESET_TIERS_FALLBACK = [25, 50, 100, 250, 500, 1000, 5000];
const LOW_BALANCE_THRESHOLD_INR = 25;
const BONUS_BRACKETS_FALLBACK = [
    { min_inr: 100,  max_inr: 500,    bonus_pct: 15 },
    { min_inr: 501,  max_inr: 1000,   bonus_pct: 30 },
    { min_inr: 1001, max_inr: null,   bonus_pct: 50 },
];

// Returns the bonus percentage for a given recharge amount, or 0.
function bonusPctFor(amountInr, brackets) {
    const amt = Number(amountInr || 0);
    for (const b of brackets || []) {
        const lo = Number(b.min_inr || 0);
        const hi = b.max_inr == null ? null : Number(b.max_inr);
        if (amt >= lo && (hi === null || amt <= hi)) return Number(b.bonus_pct || 0);
    }
    return 0;
}

function formatMMSS(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds));
    const mm = String(Math.floor(s / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    return `${mm}:${ss}`;
}

function GurujiAvatar({ size = 'md' }) {
    const dim = size === 'sm' ? 32 : 48;
    return (
        <div
            data-testid="guruji-avatar"
            className="rounded-full overflow-hidden border border-marigold/40 shadow-[0_0_16px_rgba(245,158,11,0.25)] bg-[#3a1d0a]"
            style={{ width: dim, height: dim }}
            aria-label="Guruji"
        >
            <img
                src="/img/guruji-avatar.png"
                alt="Guruji"
                width={dim}
                height={dim}
                className="w-full h-full object-cover select-none"
                draggable={false}
            />
        </div>
    );
}

function Bubble({ role, children, kind = 'text' }) {
    const isUser = role === 'user';
    const align = isUser ? 'justify-end' : 'justify-start';
    const tint = kind === 'followup' ? 'bg-[#1c1a2b] border-marigold/30' : 'bg-elevated border-marigold/10';
    const userTint = 'bg-marigold/15 border-marigold/40 text-parchment';
    return (
        <div className={`w-full flex ${align}`}>
            <div className={`max-w-[85%] px-4 py-3 rounded-2xl border ${isUser ? userTint : tint} text-sm text-parchment whitespace-pre-wrap`}>
                {children}
            </div>
        </div>
    );
}

function VerdictBubble({ verdict, confidence, category }) {
    const tone =
        verdict === 'YES' || verdict === 'YES_WITH_DELAY' ? 'text-emerald-300' :
        verdict === 'NO' || verdict === 'NO_BUT_LATER' ? 'text-rose-300' :
        'text-amber-300';
    const label = (verdict || 'unknown').replaceAll('_', ' ');
    return (
        <Bubble role="assistant">
            <div className="flex items-center gap-3">
                <span className={`uppercase tracking-wider font-medium ${tone}`} data-testid="verdict-label">
                    {label}
                </span>
                <span className="text-xs text-slate-400">{confidence ?? 0}% confidence</span>
            </div>
            {category && <div className="mt-1 text-xs text-slate-400">Category: {category}</div>}
        </Bubble>
    );
}

function TimingBubble({ start, end, description }) {
    if (!start && !end && !description) return null;
    return (
        <Bubble role="assistant">
            <div className="text-xs uppercase tracking-wider text-marigold mb-1">Timing</div>
            {start || end ? (
                <div>{start || '—'} → {end || '—'}</div>
            ) : (
                <div className="text-slate-400">No specific window in scope · {description}</div>
            )}
        </Bubble>
    );
}

function FollowUpBubble({ prompt, chips, onChipClick }) {
    return (
        <Bubble role="assistant" kind="followup">
            <div>{prompt}</div>
            <div className="mt-2 flex flex-wrap gap-2">
                {(chips || []).map((c, i) => (
                    <button
                        key={i}
                        data-testid={`chat-chip-${i}`}
                        onClick={() => onChipClick(c)}
                        className="px-3 py-1.5 rounded-full bg-elevated border border-marigold/30 text-xs text-parchment hover:bg-marigold/10 transition-colors"
                    >
                        {c}
                    </button>
                ))}
            </div>
        </Bubble>
    );
}


export default function Chat() {
    const { user } = useAuth();
    const navigate = useNavigate();

    // ── Intake state ──────────────────────────────────────────────
    const [phase, setPhase] = useState('intake');
    const [profileId, setProfileId] = useState(null);
    const [horaryNumber, setHoraryNumber] = useState('');
    const [firstQuestion, setFirstQuestion] = useState('');
    const [starting, setStarting] = useState(false);

    // ── Wallet ───────────────────────────────────────────────────
    const [balanceInr, setBalanceInr] = useState(0);
    const [walletTiers, setWalletTiers] = useState(PRESET_TIERS_FALLBACK);
    const [bonusBrackets, setBonusBrackets] = useState(BONUS_BRACKETS_FALLBACK);
    const [rechargeOpen, setRechargeOpen] = useState(false);
    const [customAmount, setCustomAmount] = useState('');
    const [recharging, setRecharging] = useState(false);
    // Round 40n.1 — shared, region-aware recharge sheet. INR users get
    // Razorpay (existing); USD users get PayPal. Replaces the local
    // INR-only RechargeSheet that was below. The wrapper closes any
    // wallet-empty pause and refreshes the in-chat balance the moment
    // funds land — keeping the post-recharge UX identical.
    const { open: openRechargeShared } = useRecharge();
    const openRecharge = () => {
        openRechargeShared({
            onSuccess: async () => {
                try {
                    const { data } = await api.get('/wallet/balance');
                    setBalanceInr(Number(data?.balance_inr ?? 0));
                } catch { /* ignore */ }
                if (paused) {
                    setPaused(false);
                    setTimeout(() => {
                        toast.success('Resumed your chat ✓', { duration: 2500 });
                    }, 400);
                }
            },
        });
    };
    // Round 40n — currency-aware chat. INR users see ₹ balance + ₹25/min
    // pace + ₹25 low-balance threshold; USD users see $ balance + $1/min
    // + $1 low-balance threshold. `currency` is resolved at mount via
    // /region/me (auth-aware after Round 40n cache key rework).
    const [currency, setCurrency] = useState('INR');
    useEffect(() => {
        let alive = true;
        fetchRegion()
            .then((r) => { if (alive) setCurrency(r?.currency || 'INR'); })
            .catch(() => { /* default INR */ });
        return () => { alive = false; };
    }, []);
    // Per-currency low-balance threshold = 1 minute of chat. INR users
    // need ≥ ₹25, USD users need ≥ $1 to keep the session alive past
    // the next minute.
    const lowBalanceThreshold = currency === 'USD' ? 1 : LOW_BALANCE_THRESHOLD_INR;
    const perMinuteCost = currency === 'USD' ? 1 : 25;

    // ── Active session ───────────────────────────────────────────
    const [session, setSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [elapsedSec, setElapsedSec] = useState(0);
    const [paused, setPaused] = useState(false);
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);
    const [endSummary, setEndSummary] = useState(null);

    // ── Post-session rating panel state (1–5 stars; comment if ≤3)
    const [ratingValue, setRatingValue] = useState(0);
    const [ratingHover, setRatingHover] = useState(0);
    const [ratingComment, setRatingComment] = useState('');
    const [ratingSubmitting, setRatingSubmitting] = useState(false);
    const [ratingSubmitted, setRatingSubmitted] = useState(false);

    // BTR nudge — opens the RectifyBirthTimeModal from inside the chat
    // when the user taps the subtle "Reading uses approximate birth
    // time · refine" chip beneath a natal event reply. The modal is
    // the same one used on the dashboard tile; embedding it here keeps
    // the conversion path one-tap from the moment of relevance (right
    // after the user has just received a reading whose precision is
    // capped by approximate BT).
    const [btrOpen, setBtrOpen] = useState(false);

    // BTR nudge — show at most once per chat session. We pick the FIRST
    // assistant message in the current `messages` array whose chart_meta
    // declares (mode='natal', birth_time_confidence='approximate'); the
    // chip renders below that message only. Subsequent approximate-natal
    // events in the same session stay un-nudged so users don't feel
    // hounded. A brand-new session resets messages, which auto-resets
    // this memo.
    const btrNudgeMessageId = useMemo(() => {
        for (const m of messages) {
            if (m.role !== 'assistant') continue;
            const c = m.content || {};
            if (c.type !== 'event' || !Array.isArray(c.bubbles)) continue;
            const meta = c.chart_meta || {};
            if (meta.mode === 'natal' && meta.birth_time_confidence === 'approximate') {
                return m.id;
            }
        }
        return null;
    }, [messages]);

    // ── Pending-message timeout tracking (Fix 6 & 7) ──────────────
    // Single pending assistant message at a time. We measure how long
    // it's been pending and surface escalating UI cues:
    //   10s → swap placeholder text to "deeper layers" copy
    //   20s → show manual retry button + interrupted-reading message
    //
    // Round 40 (bug fix): the 15s SILENT auto-retry was removed. It
    // had a race condition — if the original pending message resolved
    // between the retry-fire and the retry-response, the user saw an
    // extra Guruji reply they never asked for, and the wallet was
    // double-charged. The 20s MANUAL retry button is now the sole
    // recovery path. Backend resilience + 1.5s polling on the pending
    // message id mean genuine stalls are rare; the manual button is
    // sufficient when they happen.
    const [pendingStartedAt, setPendingStartedAt] = useState(null);
    const [pendingElapsed, setPendingElapsed] = useState(0);
    const lastUserSendRef = useRef(null); // { text, isChip }

    // ── Horary number picker — opens when a pending message resolves
    // to {kind:'horary_required'}. The user picks 1–249, we POST
    // /chat/message/{id}/horary which re-fires the engine with that
    // number; the pending placeholder gets reset to 'pending' and the
    // normal polling loop resumes.
    const [horaryPickerMessageId, setHoraryPickerMessageId] = useState(null);
    const [horaryPickerInput, setHoraryPickerInput] = useState('');
    const [horaryPickerSubmitting, setHoraryPickerSubmitting] = useState(false);

    // Tick loop runs every 6s — drains the wallet by ₹2.50 per tick.
    // We surface the elapsed timer and balance to the user but never
    // expose the literal "every 6 seconds" cadence in any copy.
    const tickCounterRef = useRef(0);
    const startedAtRef = useRef(null);
    const messagesEndRef = useRef(null);

    // ── Bootstrap: load balance + recharge tiers ─────────────────
    useEffect(() => {
        (async () => {
            try {
                const [b, t] = await Promise.all([
                    api.get('/wallet/balance'),
                    api.get('/payments/wallet/tiers'),
                ]);
                setBalanceInr(b.data?.balance_inr ?? 0);
                setWalletTiers(t.data?.presets_inr ?? PRESET_TIERS_FALLBACK);
                if (Array.isArray(t.data?.bonus_brackets)) {
                    setBonusBrackets(t.data.bonus_brackets);
                }
            } catch (e) {
                // Wallet endpoints may 401 if not logged in — handled by Protected
            }
        })();
    }, []);

    // ── Scroll behaviour ─────────────────────────────────────────
    // Goal: when the user sends a message we anchor THAT message to
    // the top of the viewport so they read the upcoming Guruji answer
    // top-down at their own pace. We do NOT auto-scroll to bottom on
    // assistant replies (the previous behaviour jerked the view to
    // the last bubble, making the user miss the top of long answers).
    const prevMsgCountRef = useRef(0);
    useEffect(() => {
        const prev = prevMsgCountRef.current;
        const curr = messages.length;
        prevMsgCountRef.current = curr;
        if (curr === 0) return;

        const last = messages[curr - 1];
        // First paint or bulk replacement (session swap, history rehydrate)
        // → jump to the end so users land at the most recent activity.
        if (prev === 0 || curr - prev > 1) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
            return;
        }
        // Exactly one new message appeared. Two cases:
        //   • user message → scroll it to the TOP so the user reads
        //     their own question + the upcoming Guruji answer
        //     top-down.
        //   • assistant message → DO NOT auto-scroll. The user is
        //     still on their question; let them scroll down at their
        //     own pace.
        if (last.role === 'user') {
            const el = document.getElementById(`chat-msg-${last.id}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [messages]);

    // ── Live MM:SS display timer (1s, decoupled from billing) ────
    useEffect(() => {
        if (phase !== 'chat' || !startedAtRef.current) return;
        const t = setInterval(() => {
            setElapsedSec(Math.floor((Date.now() - startedAtRef.current) / 1000));
        }, 1000);
        return () => clearInterval(t);
    }, [phase]);

    // ── Billing tick (6s) ────────────────────────────────────────
    // Drains wallet at ₹2.50 per tick (= ₹25/minute). Cadence is
    // intentionally NOT surfaced in copy — only the timer + balance.
    useEffect(() => {
        if (phase !== 'chat' || !session || paused) return;
        const interval = setInterval(async () => {
            tickCounterRef.current += 1;
            try {
                const { data } = await api.post('/chat/tick', {
                    session_id: session.session_id,
                    tick_id: `${session.session_id}-${tickCounterRef.current}`,
                });
                if (data.paused) {
                    setPaused(true);
                    setBalanceInr(data.balance_inr ?? 0);
                    openRecharge();
                } else {
                    setBalanceInr(data.balance_inr ?? 0);
                }
            } catch {
                // Network blip — next tick will reconcile.
            }
        }, TICK_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [phase, session, paused]);

    // ── Paused-state auto-resume watcher (Round 40f polish) ──────
    // When the session is paused, the regular billing tick is off — so
    // a webhook-delivered recharge (Cloudflare 520 dropped the verify
    // call; backend's Razorpay webhook + reconcile flow credited the
    // wallet anyway) would otherwise leave the user staring at the
    // "paused" panel forever. Poll the wallet balance every 5s while
    // paused; the moment we see funds back, flip paused → false so the
    // composer + billing tick come back online without the user having
    // to refresh or re-click anything. The interval is intentionally
    // cheap (one GET, no engine work) so leaving it running during a
    // paused session is fine.
    useEffect(() => {
        if (phase !== 'chat' || !paused) return;
        const poll = setInterval(async () => {
            try {
                const { data } = await api.get('/wallet/balance');
                const newBalanceInr = Number(data?.balance_inr || 0);
                if (newBalanceInr > 0) {
                    setBalanceInr(newBalanceInr);
                    setPaused(false);
                    setRechargeOpen(false);
                    toast.success('Wallet topped up — chat resumed ✓', { duration: 2800 });
                }
            } catch {
                // Quiet — try again on the next interval.
            }
        }, 5000);
        return () => clearInterval(poll);
    }, [phase, paused]);

    // ── Poll pending Guruji assistant messages ───────────────────
    // After /chat/start or /chat/message returns a `{kind:'pending'}`
    // placeholder, poll /api/chat/message/{id} every 1.5s until the
    // backend has the engine + LLM result written. Then swap the
    // pending placeholder for the final content in-place.
    useEffect(() => {
        if (phase !== 'chat') return;
        const pendingMsg = [...messages].reverse().find(
            (m) => m.role === 'assistant' && (m.content || {}).kind === 'pending'
        );
        if (!pendingMsg) return;
        const poll = setInterval(async () => {
            try {
                const { data } = await api.get(`/chat/message/${pendingMsg.id}`);
                if (!data.pending && data.message) {
                    setMessages((prev) => prev.map(
                        (m) => (m.id === pendingMsg.id ? data.message : m),
                    ));
                    clearInterval(poll);
                }
            } catch {
                // transient — keep polling
            }
        }, 1500);
        return () => clearInterval(poll);
    }, [messages, phase]);

    // ── Open the horary picker when a pending message resolves to
    // `horary_required`. The picker submission re-fires the engine via
    // /chat/message/{id}/horary; we close the picker as soon as the
    // backend flips the message back to {kind:'pending'} (the normal
    // polling loop above handles the rest).
    useEffect(() => {
        if (phase !== 'chat') return;
        const horaryMsg = [...messages].reverse().find(
            (m) => m.role === 'assistant' && (m.content || {}).kind === 'horary_required'
        );
        if (horaryMsg && horaryPickerMessageId !== horaryMsg.id) {
            setHoraryPickerMessageId(horaryMsg.id);
            setHoraryPickerInput('');
        } else if (!horaryMsg && horaryPickerMessageId) {
            setHoraryPickerMessageId(null);
            setHoraryPickerInput('');
        }
    }, [messages, phase, horaryPickerMessageId]);

    const submitHoraryNumber = async () => {
        const n = Number(horaryPickerInput);
        if (!horaryPickerMessageId) return;
        if (!Number.isInteger(n) || n < 1 || n > 249) {
            toast.error('Please pick a number between 1 and 249.');
            return;
        }
        setHoraryPickerSubmitting(true);
        try {
            await api.post(`/chat/message/${horaryPickerMessageId}/horary`, {
                horary_number: n,
            });
            // Optimistically flip the placeholder to pending locally so
            // the polling effect picks it up without waiting a full
            // round-trip.
            setMessages((prev) => prev.map((m) =>
                m.id === horaryPickerMessageId
                    ? { ...m, content: { kind: 'pending', text: 'Guruji is reading the chart…' } }
                    : m
            ));
            setHoraryPickerMessageId(null);
            setHoraryPickerInput('');
            // Reset pending escalation clock so 10/20s tracker is fresh.
            setPendingStartedAt(Date.now());
            setPendingElapsed(0);
        } catch (e) {
            toast.error(readErr(e, 'Could not submit horary number'));
        } finally {
            setHoraryPickerSubmitting(false);
        }
    };

    // ── Poll narration for the latest assistant message ──────────
    useEffect(() => {
        if (phase !== 'chat') return;
        const last = [...messages].reverse().find(m => m.role === 'assistant' && m.question_id);
        const firstBubble = last?.content?.bubbles?.[0];
        if (!last || !firstBubble?.narration_pending) return;
        const poll = setInterval(async () => {
            try {
                const { data } = await api.get(`/astrology/prediction/${last.question_id}`);
                if (!data?.narration_pending && data?.prediction_text) {
                    setMessages(prev => prev.map(m => {
                        if (m.id !== last.id) return m;
                        const newBubbles = [...(m.content?.bubbles || [])];
                        newBubbles[0] = {
                            ...newBubbles[0],
                            text: data.prediction_text,
                            narration_pending: false,
                        };
                        return { ...m, content: { ...m.content, bubbles: newBubbles } };
                    }));
                    clearInterval(poll);
                }
            } catch { /* keep polling */ }
        }, 2500);
        return () => clearInterval(poll);
    }, [messages, phase]);

    // ── Pending escalation: enter / exit / 1s ticker (Fix 6 & 7) ──
    useEffect(() => {
        const hasPending = messages.some(
            (m) => m.role === 'assistant' && (m.content || {}).kind === 'pending'
        );
        if (hasPending && pendingStartedAt === null) {
            setPendingStartedAt(Date.now());
            setPendingElapsed(0);
        } else if (!hasPending && pendingStartedAt !== null) {
            setPendingStartedAt(null);
            setPendingElapsed(0);
        }
    }, [messages, pendingStartedAt]);

    useEffect(() => {
        if (pendingStartedAt === null) return;
        const t = setInterval(() => {
            setPendingElapsed(Math.floor((Date.now() - pendingStartedAt) / 1000));
        }, 1000);
        return () => clearInterval(t);
    }, [pendingStartedAt]);

    // Manual retry handler — drops the stale pending placeholder and
    // re-fires onSend with the last user text. Resets pending clock.
    // Uses isRetry=true so the original user bubble isn't duplicated.
    const onManualRetry = () => {
        if (!session || !lastUserSendRef.current || sending) return;
        const { text, isChip } = lastUserSendRef.current;
        setMessages((prev) =>
            prev.filter(
                (m) => !(m.role === 'assistant' && (m.content || {}).kind === 'pending')
            )
        );
        setPendingStartedAt(null);
        setPendingElapsed(0);
        onSend(text, { isChip, isRetry: true });
    };

    // ── Start a session ──────────────────────────────────────────
    const onStart = async () => {
        if (!firstQuestion.trim()) {
            toast.error('Please type your question first.');
            return;
        }
        if (balanceInr < lowBalanceThreshold) {
            openRecharge();
            toast.error(`Please recharge at least ${formatPrice(lowBalanceThreshold, currency)} to begin a chat.`);
            return;
        }
        setStarting(true);
        try {
            const payload = {
                profile_id: profileId || null,
                first_question: firstQuestion.trim(),
                horary_number: horaryNumber ? Number(horaryNumber) : null,
            };
            const { data } = await api.post('/chat/start', payload);
            setSession(data);
            setMessages(data.messages || []);
            startedAtRef.current = Date.parse(data.started_at) || Date.now();
            setBalanceInr(data.balance_inr ?? balanceInr);
            setPhase('chat');
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (detail?.code === 'insufficient_balance') {
                // Round 48d — Show the backend's exact min-balance
                // message + open the recharge sheet in one shot.
                toast.error(detail.message || 'Please recharge to chat with Guruji.');
                openRecharge();
            } else if (detail?.code === 'horary_number_required') {
                toast.error('Pick a horary number (1–249) for this question.');
            } else {
                toast.error(readErr(e, 'Could not start chat'));
            }
        } finally {
            setStarting(false);
        }
    };

    // ── Send a follow-up message ─────────────────────────────────
    // `isChip` short-circuits the substring heuristic on the backend
    // so chip taps always route to the lightweight follow-up path
    // (no engine re-run, just Guruji LLM).
    //
    // `isRetry` indicates this is a re-send of the previous user text
    // (silent auto-retry at 15s, or manual retry tap at 20s). On retry
    // we skip the duplicate user_message append — the original user
    // bubble is already in the messages array. Any stale pending
    // assistant placeholder is stripped first to preserve the
    // "single pending bubble at a time" invariant.
    const onSend = async (textOverride, { isChip = false, isRetry = false } = {}) => {
        const text = (textOverride ?? reply).trim();
        if (!text || !session || paused) return;
        // Capture for silent auto-retry / manual retry (Fix 7). Chip taps
        // are also retryable — same text + flag re-fire is fine.
        lastUserSendRef.current = { text, isChip: !!isChip };
        setSending(true);
        try {
            const { data } = await api.post('/chat/message', {
                session_id: session.session_id,
                text,
                horary_number: horaryNumber ? Number(horaryNumber) : null,
                is_chip: !!isChip,
            });
            setMessages((prev) => {
                // Strip ALL existing pending assistant placeholders before
                // appending — guarantees a single pending bubble in DOM.
                const cleaned = prev.filter(
                    (m) => !(m.role === 'assistant' && (m.content || {}).kind === 'pending')
                );
                // On retry, the original user bubble is already in `cleaned`
                // (it was never pending). Only append the fresh assistant
                // pending. On a regular send, append both as usual.
                return isRetry
                    ? [...cleaned, data.assistant_message]
                    : [...cleaned, data.user_message, data.assistant_message];
            });
            if (!isRetry) setReply('');
        } catch (e) {
            // Round 48d — Global chat-continue floor. When the backend
            // rejects the message with insufficient_balance (wallet
            // below ₹50 / $3), surface the exact min-recharge line
            // AND pop the recharge sheet so the user can top up
            // without leaving the chat.
            const detail = e.response?.data?.detail;
            if (detail?.code === 'insufficient_balance') {
                toast.error(detail.message || 'Please recharge to continue chatting.');
                openRecharge();
            } else {
                toast.error(readErr(e, 'Could not send message'));
            }
        } finally {
            setSending(false);
        }
    };

    // Chip taps: send directly (no composer step), flagged so the
    // server takes the no-engine follow-up path.
    const onChipTap = (chipText) => onSend(chipText, { isChip: true });

    // ── End the session ──────────────────────────────────────────
    const onEnd = async () => {
        if (!session) return;
        try {
            const { data } = await api.post('/chat/end', { session_id: session.session_id });
            setEndSummary(data);
            // Reset rating panel on every fresh end so re-opening the
            // dialog never carries stale state from a prior session.
            setRatingValue(0);
            setRatingHover(0);
            setRatingComment('');
            setRatingSubmitting(false);
            setRatingSubmitted(false);
        } catch (e) {
            toast.error(readErr(e, 'Could not end session'));
        }
    };

    // ── Submit rating + (optional) comment ────────────────────────
    const submitRating = async () => {
        if (!session || ratingValue < 1 || ratingValue > 5) return;
        const trimmed = ratingComment.trim();
        if (ratingValue <= 3 && !trimmed) {
            toast.error('Please share a quick note so we can improve.');
            return;
        }
        setRatingSubmitting(true);
        try {
            await api.post(`/chat/session/${session.session_id}/rating`, {
                rating: ratingValue,
                comment: trimmed || null,
            });
            setRatingSubmitted(true);
            toast.success('Thanks for the feedback 🙏');
        } catch (e) {
            toast.error(readErr(e, 'Could not save rating'));
        } finally {
            setRatingSubmitting(false);
        }
    };

    // ── Recharge flow ────────────────────────────────────────────
    // Round 40n.1 — `onRecharge` removed. The shared RechargeProvider
    // handles INR (Razorpay) + USD (PayPal) provider branching and
    // the same wallet-empty pause/resume UX via the openRecharge
    // wrapper above.

    // ─────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────

    if (phase === 'intake') {
        return (
            <div className="max-w-2xl mx-auto p-6 lg:p-10" data-testid="chat-intake">
                <div className="mb-8 flex items-center gap-3">
                    <GurujiAvatar />
                    <div>
                        <h1 className="font-serif text-3xl text-parchment">Chat with Guruji</h1>
                        <p className="text-sm text-slate-400">
                            {currency === 'USD' ? '$1 / minute' : '₹25 / minute'} · pause anytime · top up when you want to keep going
                        </p>
                    </div>
                </div>

                <Card className="bg-surface/40 border border-marigold/15 p-6 space-y-5">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-marigold mb-2">Whose chart?</div>
                        <ProfilePicker value={profileId} onChange={setProfileId} />
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-wider text-marigold mb-2">
                            Horary number (1–249) — optional, only if asked
                        </div>
                        <Input
                            type="number"
                            min={1}
                            max={249}
                            value={horaryNumber}
                            onChange={(e) => setHoraryNumber(e.target.value)}
                            placeholder="e.g. 117"
                            data-testid="chat-horary-input"
                        />
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-wider text-marigold mb-2">Your question</div>
                        <Textarea
                            rows={4}
                            value={firstQuestion}
                            onChange={(e) => setFirstQuestion(e.target.value)}
                            placeholder="What is on your mind today?"
                            data-testid="chat-first-question"
                        />
                    </div>

                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-slate-300">
                            <Wallet size={14} className="text-marigold" />
                            <span data-testid="chat-balance-inline">Wallet: {formatPrice(balanceInr, currency)}</span>
                        </div>
                        <button
                            data-testid="chat-open-recharge"
                            onClick={() => openRecharge()}
                            className="text-marigold underline-offset-2 hover:underline"
                        >
                            Recharge
                        </button>
                    </div>

                    <Button
                        data-testid="chat-start-btn"
                        disabled={starting || !firstQuestion.trim()}
                        onClick={onStart}
                        className="w-full bg-marigold hover:bg-marigold/90 text-obsidian font-medium"
                    >
                        {starting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Sparkles size={16} className="mr-2" />}
                        Begin chat
                    </Button>
                </Card>

                {/* Round 40n.1 — local RechargeSheet removed; the
                    shared RechargeProvider modal at the AppShell level
                    handles INR + USD with the right provider. */}
            </div>
        );
    }

    // ── Chat phase ───────────────────────────────────────────────
    const isLow = balanceInr < lowBalanceThreshold && balanceInr > 0;
    const isEmpty = balanceInr <= 0;
    // Estimated minutes remaining at the per-currency per-minute rate.
    // Rounded DOWN so the user is never over-promised time.
    const estMinutesLeft = Math.floor(balanceInr / perMinuteCost);
    const minutesLabel = isEmpty
        ? null
        : estMinutesLeft >= 1
            ? `~${estMinutesLeft} min left`
            : '< 1 min left';

    return (
        <div className="flex flex-col h-[calc(100dvh-68px)] lg:h-screen max-w-3xl mx-auto" data-testid="chat-room">
            {/* Top bar — sticky so the Guruji avatar, balance pill,
                Recharge button, session timer and End-session affordance
                stay pinned as the message thread grows.

                Round 40o.4 fix: previously this was `sticky top-0` which
                collided with AppShell's mobile top bar (also `sticky
                top-0`, z-30). The AppShell bar visually covered the
                chat header on iPhone — looked like the header had
                "disappeared" once the user scrolled. Offset to
                `top-[68px]` on mobile (= AppShell mobile header height,
                py-4 + 36px content + 1px border) so the chat header
                pins immediately below the AppShell bar. Desktop is
                a sidebar layout — no top bar — so `lg:top-0`.

                Round 42c fix: the chat root height now subtracts BOTH
                the AppShell top bar (68px) AND the mobile bottom nav
                (~72px: py-3 + icon + label). Previously the composer
                rendered ~70px below the visible area on iPhone with a
                dead band of unused space above the bottom nav. The
                desktop layout is unchanged (sidebar, no bottom nav). */}
            <header className="sticky top-[68px] lg:top-0 z-20 px-4 lg:px-6 py-3 border-b border-marigold/10 bg-obsidian/95 backdrop-blur-xl flex items-center gap-3">
                <GurujiAvatar size="sm" />
                <div className="flex-1">
                    <div className="text-sm text-parchment font-medium">Guruji</div>
                    <div className="text-xs text-emerald-400 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> online
                    </div>
                </div>
                <div className="flex items-center gap-3 md:gap-4 text-sm">
                    <div className="flex flex-col items-end leading-tight" data-testid="chat-balance">
                        <div className="flex items-center gap-1.5">
                            <Wallet size={14} className={isEmpty ? 'text-rose-400' : isLow ? 'text-amber-400' : 'text-marigold'} />
                            <span className={isEmpty ? 'text-rose-400' : isLow ? 'text-amber-400' : 'text-parchment'}>
                                {formatPrice(balanceInr, currency)}
                            </span>
                        </div>
                        {minutesLabel && (
                            <span
                                data-testid="chat-minutes-left"
                                className={`text-[10px] ${isLow || isEmpty ? 'text-rose-300/80' : 'text-slate-500'}`}
                            >
                                {minutesLabel}
                            </span>
                        )}
                    </div>
                    {/* Round 40f — persistent Recharge affordance in the
                        chat header. Always visible so users have a clear
                        path to top up at any time, not only after the
                        auto-popup. Highlighted styling when paused/empty
                        so it's impossible to miss once the wallet drains. */}
                    <button
                        type="button"
                        onClick={() => openRecharge()}
                        data-testid="chat-header-recharge-btn"
                        className={
                            (isEmpty || paused)
                                ? 'px-3 py-1.5 rounded-full bg-marigold text-obsidian text-xs font-medium hover:bg-marigold/90 transition-colors animate-pulse'
                                : isLow
                                    ? 'px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-200 text-xs hover:bg-amber-500/30 transition-colors'
                                    : 'px-3 py-1.5 rounded-full bg-elevated border border-marigold/25 text-xs text-parchment/90 hover:border-marigold/50 hover:text-marigold transition-colors'
                        }
                    >
                        + Recharge
                    </button>
                    <div className="flex items-center gap-1.5" data-testid="chat-timer">
                        <Clock size={14} className={isLow || isEmpty ? 'text-rose-400' : 'text-slate-400'} />
                        <span className={isLow || isEmpty ? 'text-rose-400' : 'text-parchment'}>
                            {formatMMSS(elapsedSec)}
                        </span>
                    </div>
                    <button
                        data-testid="chat-end-btn"
                        onClick={onEnd}
                        className="text-xs text-slate-400 hover:text-rose-400 underline-offset-2 hover:underline"
                    >
                        End session
                    </button>
                </div>
            </header>

            {/* Banners — low + empty balance nudges. Copy never references the
                6-second cadence; only the timing concept (recharge / paused). */}
            {isLow && !isEmpty && (
                <div className="px-4 py-2 bg-amber-500/15 border-b border-amber-500/30 text-amber-200 text-xs flex items-center justify-between" data-testid="chat-low-banner">
                    <span>Low balance — top up to keep the conversation going.</span>
                    <button onClick={() => openRecharge()} className="underline text-amber-100 ml-3" data-testid="chat-low-banner-cta">
                        Recharge
                    </button>
                </div>
            )}
            {isEmpty && (
                <div className="px-4 py-2 bg-rose-500/15 border-b border-rose-500/30 text-rose-200 text-xs flex items-center justify-between" data-testid="chat-empty-banner">
                    <span>Wallet is empty — session paused. Recharge to continue.</span>
                    <button onClick={() => openRecharge()} className="underline text-rose-100 ml-3" data-testid="chat-empty-banner-cta">
                        Recharge
                    </button>
                </div>
            )}

            {/* Messages
                Round 42d — `pb-28 lg:pb-0` reserves space at the bottom
                of the scroll area equal to the fixed composer height so
                the last message never sits behind the pinned composer. */}
            <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 pb-28 lg:pb-4 space-y-3" data-testid="chat-messages">
                {messages.map((m) => (
                    <div key={m.id} id={`chat-msg-${m.id}`}>
                        <MessageRenderer
                            m={m}
                            onChipClick={onChipTap}
                            pendingElapsed={pendingElapsed}
                            onManualRetry={onManualRetry}
                            canRetry={!!lastUserSendRef.current}
                            onRefineBT={() => setBtrOpen(true)}
                            showBtrNudgeOnThisMessage={m.id === btrNudgeMessageId}
                        />
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>

            {/* Composer.
                If a horary_required bubble is active anywhere in the
                thread, the picker modal is the only valid input path —
                we hard-disable the composer here as a safety net in case
                the modal overlay fails to grab the user's attention.
                The placeholder explains why.

                Round 40f — when the session is PAUSED (wallet empty),
                we swap the composer for a dedicated "Recharge to
                continue" panel. Previously the composer was just
                disabled with a quiet placeholder, which left users
                stranded if they had dismissed the auto-popup. Now the
                same real estate is taken by an unmissable call to
                action so there's always a one-tap path back to chat.

                Round 42d — composer is `fixed` to the viewport bottom on
                mobile (just above the AppShell bottom nav at 72px) so it
                never scrolls away. On desktop (lg+) we keep it inline
                with the flex layout because the sidebar layout has no
                bottom nav. The fixed positioning is scoped to mobile
                via `lg:static` and the bottom offset uses
                `safe-area-inset-bottom` so the composer clears the
                home-bar on notched iPhones. */}
            {paused ? (
                <div
                    data-testid="chat-paused-panel"
                    className="fixed bottom-[72px] left-0 right-0 z-30 lg:static lg:bottom-auto border-t border-rose-500/30 bg-rose-500/10 backdrop-blur-xl p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pb-[max(1rem,env(safe-area-inset-bottom))] lg:pb-4"
                >
                    <div className="flex-1 text-sm leading-snug">
                        <div className="text-rose-200 font-medium">Your wallet is empty</div>
                        <div className="text-rose-300/80 text-xs mt-0.5">
                            The conversation is paused. Top up to keep talking with Guruji — your chat history is safe.
                        </div>
                    </div>
                    <Button
                        type="button"
                        onClick={() => openRecharge()}
                        data-testid="chat-paused-recharge-btn"
                        className="bg-marigold hover:bg-marigold/90 text-obsidian font-medium shrink-0"
                    >
                        <Wallet size={14} className="mr-2" />
                        Recharge wallet
                    </Button>
                </div>
            ) : (
            <div className="fixed bottom-[72px] left-0 right-0 z-30 lg:static lg:bottom-auto border-t border-marigold/10 bg-obsidian/95 backdrop-blur-xl p-3 flex items-end gap-2 max-w-3xl mx-auto pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:pb-3">
                <Textarea
                    rows={2}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    disabled={sending || !!horaryPickerMessageId}
                    placeholder={
                        horaryPickerMessageId
                            ? 'Pick a horary number to continue…'
                            : 'Reply to Guruji…'
                    }
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
                    }}
                    data-testid="chat-input"
                    className="flex-1 resize-none"
                />
                <Button
                    onClick={() => onSend()}
                    disabled={sending || !reply.trim() || !!horaryPickerMessageId}
                    data-testid="chat-send-btn"
                    className="bg-marigold hover:bg-marigold/90 text-obsidian h-12 w-12 p-0"
                    aria-label="Send"
                >
                    {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                </Button>
            </div>
            )}

            {/* Round 40n.1 — local RechargeSheet removed; shared
                RechargeProvider modal handles INR + USD.
                Round 42b — bottom duplicate "End session & rate" row
                removed. The header already exposes End session top-right,
                and on mobile the duplicate ate ~50px of reading area
                below the composer. */}

            {/* End-of-session rating panel. Shows the session
                summary at the top and a 5-star rating below. Stars
                ≤ 3 reveal a required comment textarea so we can
                capture *why* a reading underwhelmed. The user can
                also skip rating and just close the dialog — we don't
                gate dashboard navigation on the rating itself. */}
            <Dialog open={!!endSummary} onOpenChange={() => setEndSummary(null)}>
                <DialogContent data-testid="chat-end-summary" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {ratingSubmitted ? 'Thanks for the feedback' : 'Rate this conversation'}
                        </DialogTitle>
                    </DialogHeader>
                    {endSummary && (
                        <div className="space-y-4">
                            {/* Summary line */}
                            <div className="rounded-lg border border-marigold/15 bg-surface/60 px-3 py-2 text-xs text-slate-300 space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-slate-400">Duration</span>
                                    <span className="text-parchment">{formatMMSS(endSummary.duration_seconds || 0)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-400">Spent</span>
                                    <span className="text-parchment">{formatPrice(endSummary.total_spent_inr || 0, currency)}</span>
                                </div>
                                {endSummary.first_question && (
                                    <div className="pt-1 text-slate-500 italic line-clamp-2">
                                        &ldquo;{endSummary.first_question}&rdquo;
                                    </div>
                                )}
                            </div>

                            {!ratingSubmitted && (
                                <>
                                    {/* Star row — 5 buttons w/ hover preview. */}
                                    <div>
                                        <div className="text-sm text-slate-300 mb-2">
                                            How was your reading with Guruji?
                                        </div>
                                        <div
                                            className="flex items-center gap-1.5"
                                            data-testid="chat-rating-stars"
                                            onMouseLeave={() => setRatingHover(0)}
                                        >
                                            {[1, 2, 3, 4, 5].map((n) => {
                                                const filled = (ratingHover || ratingValue) >= n;
                                                return (
                                                    <button
                                                        key={n}
                                                        type="button"
                                                        data-testid={`chat-rating-star-${n}`}
                                                        onMouseEnter={() => setRatingHover(n)}
                                                        onClick={() => setRatingValue(n)}
                                                        className="p-1 transition-transform hover:scale-110"
                                                        aria-label={`Rate ${n} ${n === 1 ? 'star' : 'stars'}`}
                                                    >
                                                        <Star
                                                            size={28}
                                                            className={filled ? 'fill-marigold text-marigold' : 'text-slate-500'}
                                                            strokeWidth={1.5}
                                                        />
                                                    </button>
                                                );
                                            })}
                                            {ratingValue > 0 && (
                                                <span
                                                    className="ml-2 text-xs text-slate-400"
                                                    data-testid="chat-rating-value"
                                                >
                                                    {ratingValue} / 5
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Comment box reveals only for ratings ≤ 3. */}
                                    {ratingValue > 0 && ratingValue <= 3 && (
                                        <div data-testid="chat-rating-comment-wrap">
                                            <label className="block text-sm text-slate-300 mb-1">
                                                What could have been better?
                                            </label>
                                            <Textarea
                                                rows={3}
                                                value={ratingComment}
                                                onChange={(e) => setRatingComment(e.target.value)}
                                                placeholder="e.g. The verdict felt vague, or the timing window was too wide…"
                                                data-testid="chat-rating-comment"
                                                maxLength={2000}
                                            />
                                            <div className="mt-1 text-[11px] text-slate-500">
                                                Required for low ratings — helps us improve.
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex justify-between pt-2 gap-2">
                                        <Button
                                            variant="outline"
                                            onClick={() => navigate('/app')}
                                            data-testid="chat-rating-skip"
                                            className="flex-1"
                                        >
                                            Skip
                                        </Button>
                                        <Button
                                            onClick={submitRating}
                                            disabled={
                                                ratingSubmitting
                                                || ratingValue < 1
                                                || (ratingValue <= 3 && !ratingComment.trim())
                                            }
                                            data-testid="chat-rating-submit"
                                            className="flex-1 bg-marigold text-obsidian hover:bg-marigold/90"
                                        >
                                            {ratingSubmitting ? (
                                                <Loader2 className="animate-spin mr-2" size={14} />
                                            ) : null}
                                            Submit rating
                                        </Button>
                                    </div>
                                </>
                            )}

                            {ratingSubmitted && (
                                <>
                                    <p className="text-sm text-slate-300" data-testid="chat-rating-thanks">
                                        Your feedback is logged and will shape future readings.
                                    </p>
                                    <div className="pt-2 flex gap-2">
                                        <Button
                                            onClick={() => navigate('/app')}
                                            className="flex-1 bg-marigold text-obsidian hover:bg-marigold/90"
                                            data-testid="chat-rating-done-dashboard"
                                        >
                                            Back to dashboard
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => window.location.reload()}
                                            className="flex-1"
                                            data-testid="chat-rating-done-newchat"
                                        >
                                            New chat
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* BTR modal — opened by the subtle chip beneath any
                natal event reading whose chart was cast with
                approximate birth time. On success the user's
                birth_time_confidence is rectified server-side; the
                in-memory `user` context is refreshed so subsequent
                readings drop the nudge automatically. */}
            <RectifyBirthTimeModal
                open={btrOpen}
                onOpenChange={setBtrOpen}
                onSuccess={() => setBtrOpen(false)}
            />

            {/* Horary number picker — opens when the engine flags
                {kind: 'horary_required'} on a pending message. The
                user picks 1–249; we re-fire the engine via
                /chat/message/{id}/horary. The Dialog is not
                user-dismissable (no onOpenChange close path) — they
                must pick a number for the reading to proceed. */}
            <Dialog
                open={!!horaryPickerMessageId}
                onOpenChange={(open) => {
                    // Allow programmatic close (after submit) but block
                    // backdrop-click / ESC dismiss so the user can't get
                    // stuck with an un-readable assistant bubble.
                    if (open) return;
                }}
            >
                <DialogContent
                    data-testid="chat-horary-picker"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                    hideClose
                    className="sm:max-w-md border-marigold/60 shadow-[0_0_60px_rgba(245,158,11,0.25)] backdrop-blur-md"
                >
                    <DialogHeader>
                        <DialogTitle>Pick a horary number</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            KP horary needs a number between 1 and 249 to anchor the moment of asking to a specific ascendant degree.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-slate-300 leading-relaxed">
                            For this question Guruji needs a number between
                            <span className="text-marigold"> 1 and 249</span>.
                            Pick whatever number comes to mind first —
                            that&apos;s the prashna seed for your reading.
                        </p>
                        <Input
                            type="number"
                            min={1}
                            max={249}
                            value={horaryPickerInput}
                            onChange={(e) => setHoraryPickerInput(e.target.value)}
                            placeholder="e.g. 117"
                            autoFocus
                            data-testid="chat-horary-picker-input"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    submitHoraryNumber();
                                }
                            }}
                        />
                        <Button
                            onClick={submitHoraryNumber}
                            disabled={
                                horaryPickerSubmitting
                                || !horaryPickerInput
                                || Number(horaryPickerInput) < 1
                                || Number(horaryPickerInput) > 249
                            }
                            data-testid="chat-horary-picker-submit"
                            className="w-full bg-marigold text-obsidian hover:bg-marigold/90"
                        >
                            {horaryPickerSubmitting
                                ? <Loader2 className="animate-spin mr-2" size={16} />
                                : <Sparkles size={16} className="mr-2" />}
                            Cast the chart
                        </Button>
                        <p className="text-[11px] text-slate-500">
                            Why a number? KP horary doctrine uses your chosen
                            number to anchor the moment of asking to a specific
                            ascendant degree.
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function MessageRenderer({ m, onChipClick, pendingElapsed = 0, onManualRetry, canRetry = false, onRefineBT, showBtrNudgeOnThisMessage = false }) {
    const c = m.content || {};
    if (m.role === 'user') {
        return <Bubble role="user">{c.text}</Bubble>;
    }
    // Pending placeholder while the engine + LLM run in the background.
    // Escalation per Round 40:
    //   <10s: original placeholder text
    //   10–19s: "Guruji is reading the deeper layers of your chart…"
    //   ≥20s: interrupted-reading message + manual retry button
    // The 15s silent auto-retry was removed (Round 40 fix) — it caused
    // duplicate-reply / auto-mode bugs when the original request
    // resolved between retry-fire and retry-response.
    if (c.kind === 'pending') {
        const showRetry = pendingElapsed >= 20;
        const longWait = pendingElapsed >= 10 && !showRetry;
        const placeholder = showRetry
            ? 'Something interrupted the reading — please try again.'
            : longWait
                ? 'Guruji is reading the deeper layers of your chart — just a moment…'
                : (c.text || 'Guruji is reading the chart…');
        return (
            <Bubble role="assistant">
                <div className="flex flex-col gap-2" data-testid="chat-pending-bubble">
                    <span
                        className={`italic inline-flex items-center gap-2 ${showRetry ? 'text-rose-300' : 'text-slate-400'}`}
                        data-testid={showRetry ? 'chat-pending-interrupted' : longWait ? 'chat-pending-longwait' : 'chat-pending-initial'}
                    >
                        {!showRetry && <Loader2 className="animate-spin" size={12} />}
                        {placeholder}
                    </span>
                    {showRetry && canRetry && (
                        <button
                            type="button"
                            data-testid="chat-pending-retry-btn"
                            onClick={() => onManualRetry && onManualRetry()}
                            className="self-start px-3 py-1.5 rounded-full bg-marigold/20 border border-marigold/40 text-xs text-marigold hover:bg-marigold/30 transition-colors"
                        >
                            This is taking longer than expected — tap to try again
                        </button>
                    )}
                </div>
            </Bubble>
        );
    }
    // Horary-required state — engine demanded a 1–249 number mid-chat.
    // Render a compact bubble; the picker modal is mounted at the
    // page level and opens automatically (see useEffect on messages).
    if (c.kind === 'horary_required') {
        return (
            <Bubble role="assistant">
                <div className="flex items-center gap-2" data-testid="chat-horary-required-bubble">
                    <Sparkles size={14} className="text-marigold" />
                    <span className="text-parchment">
                        {c.text || 'This question needs a horary number — pick a number between 1 and 249.'}
                    </span>
                </div>
            </Bubble>
        );
    }
    // Plain text (opening line, error states, legacy 'kind=text')
    if (c.kind === 'text' || (c.type !== 'event' && c.type !== 'followup' && Array.isArray(c.bubbles) === false && c.kind !== 'bubbles')) {
        return <Bubble role="assistant">{c.text}</Bubble>;
    }

    // New Guruji event reading: 4 plain-text bubbles + chips
    if (c.type === 'event' && Array.isArray(c.bubbles)) {
        // Subtle BTR nudge — only on the FIRST approximate-natal reading
        // in a session (parent picks the target message id via useMemo
        // over `messages`). Subsequent approximate-natal events stay
        // un-nudged to avoid fatigue.
        const showBtrNudge = showBtrNudgeOnThisMessage;
        return (
            <div className="space-y-2">
                {c.bubbles.map((b, i) => (
                    <Bubble key={i} role="assistant">{b}</Bubble>
                ))}
                {Array.isArray(c.chips) && c.chips.length > 0 && (
                    <div className="flex flex-wrap gap-2 ml-1 mt-1">
                        {c.chips.map((chip, i) => (
                            <button
                                key={i}
                                data-testid={`chat-chip-${i}`}
                                onClick={() => onChipClick(chip)}
                                className="px-3 py-1.5 rounded-full bg-elevated border border-marigold/30 text-xs text-parchment hover:bg-marigold/10 transition-colors"
                            >
                                {chip}
                            </button>
                        ))}
                    </div>
                )}
                {showBtrNudge && (
                    <button
                        type="button"
                        onClick={() => onRefineBT && onRefineBT()}
                        data-testid="chat-btr-nudge"
                        className="group ml-1 mt-2 inline-flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-marigold transition-colors"
                    >
                        <Sparkles size={11} className="opacity-60 group-hover:opacity-100" />
                        <span className="italic">
                            Cast with an approximate birth time
                        </span>
                        <span className="text-slate-600 group-hover:text-marigold/80">
                            · refine for sharper timing →
                        </span>
                    </button>
                )}
                {c.disclaimer && (
                    <div className="text-[10px] text-slate-500 ml-1 italic">
                        {c.disclaimer}
                    </div>
                )}
            </div>
        );
    }

    // New Guruji follow-up: single bubble + chips
    if (c.type === 'followup' && c.reply) {
        return (
            <div className="space-y-2">
                <Bubble role="assistant">{c.reply}</Bubble>
                {Array.isArray(c.chips) && c.chips.length > 0 && (
                    <div className="flex flex-wrap gap-2 ml-1 mt-1">
                        {c.chips.map((chip, i) => (
                            <button
                                key={i}
                                data-testid={`chat-chip-${i}`}
                                onClick={() => onChipClick(chip)}
                                className="px-3 py-1.5 rounded-full bg-elevated border border-marigold/30 text-xs text-parchment hover:bg-marigold/10 transition-colors"
                            >
                                {chip}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // Legacy structured bubbles (kept for any in-flight session resume)
    if (c.kind === 'bubbles' && Array.isArray(c.bubbles)) {
        return (
            <div className="space-y-2">
                {c.bubbles.map((b, i) => {
                    if (b.kind === 'text') {
                        return (
                            <Bubble key={i} role="assistant">
                                {b.narration_pending ? (
                                    <span className="text-slate-400 italic inline-flex items-center gap-2">
                                        <Loader2 className="animate-spin" size={12} /> {b.text}
                                    </span>
                                ) : b.text}
                            </Bubble>
                        );
                    }
                    if (b.kind === 'verdict') {
                        return <VerdictBubble key={i} verdict={b.verdict} confidence={b.confidence} category={b.category} />;
                    }
                    if (b.kind === 'timing') {
                        return <TimingBubble key={i} start={b.window_start} end={b.window_end} description={b.description} />;
                    }
                    if (b.kind === 'followup') {
                        return <FollowUpBubble key={i} prompt={b.prompt} chips={b.chips} onChipClick={onChipClick} />;
                    }
                    return null;
                })}
            </div>
        );
    }
    return null;
}


// Round 40n.1 — local RechargeSheet removed. The shared
// RechargeProvider modal (region-aware: INR via Razorpay, USD via
// PayPal) is the single source of truth for wallet top-ups across
// the app, including from inside chat.
